import BreadCrumbs from 'components/breadcrumbs'
import LoadingV2 from 'components/LoadingV2'
import PMBModalRemove from 'components/PMBModalRemove'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import PMBTooltip from 'components/PMBTooltip'
import { TableComponent as DataTable } from 'components/TableComponent'
import { useDebounce } from 'helpers'
import useIsMobile from 'hooks/useIsMobile'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { ChevronDown, Clock, Edit2, Plus, Trash2 } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Card, CardBody, Spinner } from 'reactstrap'
import { URL_WORKPLACE } from 'utils/pathUrl'
import ModalAddEditMapSchema from './ModalAddEditMapSchema'
import ModalVersionHistory from './ModalVersionHistory'
import {
  useDeleteMapSchemaMutation,
  useFetchMapSchemasQuery,
} from './mapSchemaApiSlice'
import { setFetchOption } from './mapSchemaSlice'

const ListMapSchema = () => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const { tag } = useSelector((state) => state.mapSchema)

  const isMobile = useIsMobile()

  const [searchInput, setSearchInput] = useState('')
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)
  const [isFilter, setIsFilter] = useState(false)
  const [isOpenModalAddEdit, setIsOpenModalAddEdit] = useState(false)
  const [isOpenModalVersionHistory, setIsOpenModalVersionHistory] =
    useState(false)
  const [dataSchemaById, setDataSchemaById] = useState(null)

  const [deleteMapSchema, { isLoading: isDeleting }] =
    useDeleteMapSchemaMutation()

  const { data, isLoading, refetch, isFetching } = useFetchMapSchemasQuery(
    {
      page: tag?.page ?? 0,
      size: tag?.size ?? 10,
      s: tag?.s,
      sortby: tag?.sortby,
      sortdir: tag?.sortdir,
    },
    { refetchOnMountOrArgChange: true, skip: false },
  )

  // COLUMNS
  const columns = [
    {
      name: <span>Nama Skema</span>,
      selector: 'name',
      sortable: true,
      sortField: 'name',
      grow: 2,
      cell: (row) => {
        return (
          <div className="d-flex flex-column">
            <span className="m-0 fw-semibold" data-testid={`map-schema-row-${row.id}`}>
              {row?.name}
            </span>
            {row?.description && (
              <span className="m-0 text-muted" style={{ fontSize: '0.85rem' }}>
                {row.description}
              </span>
            )}
          </div>
        )
      },
    },
    {
      name: <span>Versi Aktif</span>,
      selector: 'version',
      sortable: true,
      sortField: 'version',
      width: '120px',
      cell: (row) => {
        return (
          <span className="badge bg-primary" style={{ fontSize: '0.9rem' }}>
            v{row?.version || 1}
          </span>
        )
      },
    },
    {
      name: <span>Komposisi</span>,
      selector: 'items',
      grow: 3,
      cell: (row) => {
        return (
          <div>
            {row?.items && row.items.length > 0 ? (
              <ul className="mb-0" style={{ paddingLeft: '20px' }}>
                {row.items.slice(0, 3).map((item, idx) => (
                  <li key={idx} style={{ fontSize: '0.85rem' }}>
                    {item.quantity}x {item.itemName}
                  </li>
                ))}
                {row.items.length > 3 && (
                  <li style={{ fontSize: '0.85rem' }}>
                    +{row.items.length - 3} item lainnya
                  </li>
                )}
              </ul>
            ) : (
              <span className="text-muted">-</span>
            )}
          </div>
        )
      },
    },
    {
      name: <span>Dibuat Oleh</span>,
      selector: 'createdBy',
      grow: 1,
      cell: (row) => {
        return (
          <div className="d-flex flex-column">
            <span className="m-0 fw-semibold">{row?.createdBy || '-'}</span>
            <span className="m-0 text-muted" style={{ fontSize: '0.8rem' }}>
              {row?.createdAt
                ? moment(row.createdAt).format('DD MMM YYYY')
                : '-'}
            </span>
          </div>
        )
      },
    },
    {
      name: <span>Diubah Terakhir</span>,
      selector: 'updatedBy',
      grow: 1,
      cell: (row) => {
        return (
          <div className="d-flex flex-column">
            <span className="m-0 fw-semibold">{row?.updatedBy || '-'}</span>
            <span className="m-0 text-muted" style={{ fontSize: '0.8rem' }}>
              {row?.updatedAt
                ? moment(row.updatedAt).format('DD MMM YYYY')
                : '-'}
            </span>
          </div>
        )
      },
    },
    {
      name: '',
      width: '200px',
      right: true,
      cell: (row) => {
        return (
          <div
            className="d-flex align-items-center"
            style={{ gap: '16px', flexGrow: 1 }}
          >
            <div id={`version-${row?.id}`}>
              <Clock
                color="#6c757d"
                size={18}
                onClick={() => {
                  setDataSchemaById(row)
                  setIsOpenModalVersionHistory(true)
                }}
                style={{ cursor: 'pointer' }}
                data-testid={`map-schema-version-${row?.id}`}
              />
            </div>
            <PMBTooltip
              target={`version-${row?.id}`}
              text="Lihat Riwayat Versi"
            />

            <div id={`edit-${row?.id}`}>
              <Edit2
                color="#007BFF"
                size={18}
                onClick={() => {
                  setDataSchemaById(row)
                  setIsOpenModalAddEdit(true)
                }}
                style={{ cursor: 'pointer' }}
                data-testid={`map-schema-edit-${row?.id}`}
              />
            </div>
            <PMBTooltip target={`edit-${row?.id}`} text="Edit" />

            <div id={`remove-${row?.id}`}>
              <Trash2
                color="red"
                size={18}
                onClick={() => {
                  setIsOpenDeleteModal(true)
                  setDataSchemaById(row)
                }}
                style={{ cursor: 'pointer' }}
                data-testid={`map-schema-delete-${row?.id}`}
              />
            </div>
            <PMBTooltip target={`remove-${row?.id}`} text="Hapus" />
          </div>
        )
      },
    },
  ]

  // HANDLE SUBMIT REMOVE BY ID
  const handleSubmitRemoveById = async (id) => {
    try {
      if (!id) {
        enqueueSnackbar('Tidak ada data yang dipilih untuk dihapus', {
          variant: 'error',
        })
        return
      }

      const response = await deleteMapSchema(id)
      if (response && response?.data) {
        enqueueSnackbar(`Skema "${dataSchemaById?.name}" berhasil dihapus`, {
          variant: 'success',
        })
        setIsOpenDeleteModal(false)
        setDataSchemaById(null)
        refetch()
      }
    } catch (error) {
      enqueueSnackbar('Skema gagal dihapus', {
        variant: 'error',
      })
    }
  }

  const handleSort = (column, direction) => {
    dispatch(
      setFetchOption({
        sortby: column.sortField,
        sortdir: direction?.toUpperCase(),
      }),
    )
  }

  useEffect(() => {
    dispatch(setFetchOption({ page: 0 }))
  }, [searchInput])

  const debouncedSearch = useDebounce((value) => {
    dispatch(
      setFetchOption({
        page: 0,
        s: value,
      }),
    )
  }, 600)

  const onHandleSearchChange = (value) => {
    if (!value?.trim()) {
      setIsFilter(false)
    } else {
      setIsFilter(true)
    }
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && tag?.s.length === 0) {
      e.preventDefault()
    }
  }

  if (isLoading) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      {/* MODAL ADD/EDIT MAP SCHEMA */}
      <ModalAddEditMapSchema
        isOpen={isOpenModalAddEdit}
        toggle={() => {
          setIsOpenModalAddEdit((prev) => !prev)
          setDataSchemaById(null)
        }}
        onSuccess={refetch}
        dataSchemaById={dataSchemaById}
      />

      {/* MODAL VERSION HISTORY */}
      <ModalVersionHistory
        isOpen={isOpenModalVersionHistory}
        toggle={() => {
          setIsOpenModalVersionHistory((prev) => !prev)
          setDataSchemaById(null)
        }}
        schemaData={dataSchemaById}
      />

      {/* MODAL REMOVE MAP SCHEMA */}
      <PMBModalRemove
        isOpen={isOpenDeleteModal}
        onClose={() => {
          setIsOpenDeleteModal((prev) => !prev)
          setDataSchemaById(null)
        }}
        title={`Hapus Map Skema`}
        dataId={dataSchemaById?.id}
        desc={`Apakah Anda yakin ingin menghapus skema "${dataSchemaById?.name}" (v${dataSchemaById?.version})?`}
        isLoading={isDeleting}
        onRemoveData={(id) => handleSubmitRemoveById(id)}
        buttonTitle="Hapus"
      />

      <div className="mt-3">
        <BreadCrumbs
          breadCrumbParent={'Alat Bantu'}
          breadCrumbParentLink={URL_WORKPLACE}
          breadCrumbActive="Daftar Map Skema"
        />
      </div>

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          <DataTable
            responsive
            persistTableHead
            defaultSortFieldId={1}
            defaultSortAsc={true}
            columns={columns}
            data={data?.items || []}
            progressPending={isFetching}
            paginationTotalRows={data?.totalItems}
            pagination
            paginationServer
            paginationComponent={() => (
              <PMBPagination
                rowCount={data?.totalItems || 0}
                currentPage={tag?.page}
                rowsPerPage={tag?.size}
                setCurrentPage={(e) => {
                  dispatch(setFetchOption({ page: e }))
                }}
                setRowsPerPage={(e) => {
                  dispatch(setFetchOption({ size: e }))
                }}
                testId="map-schema-pagination"
              />
            )}
            progressComponent={
              <div style={{ margin: '100px 0px' }}>
                <Spinner />
              </div>
            }
            onSort={handleSort}
            sortIcon={<ChevronDown />}
            sortServer
            subHeader={true}
            subHeaderComponent={
              <div className="w-100">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div>
                    <h5 className="fw-semibold mb-1">Daftar Map Skema</h5>
                    <p
                      className="text-muted mb-0"
                      style={{ fontSize: '0.9rem' }}
                    >
                      Kelola skema dengan sistem versioning otomatis. Produksi
                      selalu menggunakan versi terbaru.
                    </p>
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      onClick={() => setIsOpenModalAddEdit(true)}
                      size="sm"
                      color="primary"
                      className="d-flex align-items-center gap-2"
                      data-testid="map-schema-add-button"
                    >
                      <Plus size={16} />
                      Tambah Skema
                    </Button>
                  </div>
                </div>

                <div className="my-4">
                  <PMBSearchInputGroup
                    label="Cari"
                    value={searchInput}
                    placeholder="Cari nama skema..."
                    onChange={(e) => onHandleSearchChange(e.target.value)}
                    maxWidth={isMobile ? '100%' : '20%'}
                    onKeyDown={handleKeyDown}
                    testId="map-schema-search-input"
                  />
                </div>
              </div>
            }
            noDataComponent={
              <p className="my-3 fw-semibold">
                {isFilter
                  ? 'Tidak ada data yang sesuai dengan pencarian Anda'
                  : 'Belum ada map skema yang tersedia'}
              </p>
            }
          />
        </CardBody>
      </Card>
    </div>
  )
}

export default ListMapSchema
